// src/pages/AnalyticsHubPage.tsx
//
// PR3 — Analytics hub with left-sidebar navigation. Each section is gated
// by a sub-feature from the catalog (analytics.sales / .ar / .customers /
// .inventory). Sysadmin sees every section; non-sysadmin sees only what
// they have effective access to (tenant default OR explicit user override).
//
// Layout:
//   ┌─────────────┬────────────────────────────────────┐
//   │             │ [sub-tab strip, grouped sections]  │
//   │ Overview    │ <selected section's full content>  │
//   │ Sales       │                                    │
//   │ A/R         │                                    │
//   │ Orders      │                                    │
//   │ Customers   │                                    │
//   │ Inventory   │                                    │
//   └─────────────┴────────────────────────────────────┘
//
// Grouped sections (Sales / A/R / Orders) carry sub-tabs that render as a
// horizontal strip at the top of the content panel (desktop) or as a second
// pill row under the section strip (mobile). A sub-tab is visible only if
// its feature passes the same gating as top-level sections; a grouped
// section is visible if ANY of its sub-tabs is. When exactly one sub-tab is
// visible the strip is hidden and the content renders directly.
//
// Section content reuses the existing detail tabs from AnalyticsPageV2
// (exported as SalesDetailTab / ARDetailTab) so we don't fork a second
// implementation while the page architecture settles. Overview is a new
// summary-card view that fetches dashboard-all and renders one card per
// section the user has access to; clicking a card jumps to that section.
//
// The previous DashboardV2 stays in the repo for reference but is not
// routed to from the landing page anymore.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, BarChart3, LayoutDashboard, TrendingUp,
  Receipt, Users as UsersIcon, Boxes, Truck, Bot, ChevronRight,
  ClipboardList, Wallet,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { adminApi } from '../services/adminApi';
import { apiV2 as api } from '../services/apiV2';
import type { ARSummary } from '../types';
import { isFeatureEnabled } from '../lib/featureCatalog';
import { hasAdminRole } from '../lib/sysadminRoles';
import { SalesDetailTab, ARDetailTab } from './AnalyticsPageV2';
import { SalesByRepTab } from './analytics/SalesByRepTab';
import { InventoryTab } from './analytics/InventoryTab';
import { PurchasingPipelineTab } from './analytics/PurchasingPipelineTab';
import { AiOrdersTab } from './analytics/AiOrdersTab';
import { OrderSheetTab } from './analytics/OrderSheetTab';
import { SalesTrendsTab } from './analytics/SalesTrendsTab';
import { LostBusinessTab } from './analytics/LostBusinessTab';
import { SevenVs28Tab } from './analytics/SevenVs28Tab';
import { YoYOverviewTab } from './analytics/YoYOverviewTab';
import { ArInsightsTab } from './analytics/ArInsightsTab';
import { OpsAuditTab } from './analytics/OpsAuditTab';
import { TransfersTab } from './analytics/TransfersTab';
import { SalesVsPaymentsTab } from './analytics/SalesVsPaymentsTab';
import { GpAnalysisTab } from './analytics/GpAnalysisTab';
import { MarginAnalysisTab } from './analytics/MarginAnalysisTab';
import { CustomerInsightsTab } from './analytics/CustomerInsightsTab';
import { AgingTab } from './analytics/payables/AgingTab';
import { ApBillsTab } from './analytics/payables/ApBillsTab';
import { VendorSpendTab } from './analytics/payables/VendorSpendTab';
import { VendorPriceChangesTab } from './analytics/payables/VendorPriceChangesTab';
import { VendorScorecardTab } from './analytics/payables/VendorScorecardTab';
import { QboDiffTab } from './analytics/payables/QboDiffTab';
import { InvoicesTab } from './analytics/InvoicesTab';
import { CustomerWeeksTab } from './analytics/CustomerWeeksTab';
import { CcFeeTab } from './analytics/CcFeeTab';
import { ArFocusTab } from './analytics/ArFocusTab';
import { DailySnapshotTab } from './analytics/DailySnapshotTab';
import { CustomerCountsTab } from './analytics/CustomerCountsTab';
import { useSection } from '../lib/sectionStore';

type Section = 'overview' | 'sales' | 'ar' | 'ap' | 'orders' | 'customers' | 'inventory' | 'purchasing' | 'ai_orders';

interface SubTabDef {
  key: string;
  feature: string;          // sub-tab is visible only if this feature passes
  label: string;
}

interface SectionDef {
  key: Section;
  feature: string | null;   // null = Overview (always shown if user has `analytics`).
                            // Ignored when subTabs is set — the section is visible
                            // if ANY of its sub-tabs is visible.
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  description: string;
  subTabs?: SubTabDef[];    // grouped section: secondary tab strip in the content panel
}

const SECTIONS: SectionDef[] = [
  { key: 'overview',   feature: null,                  label: 'Overview',   icon: LayoutDashboard, description: 'Snapshot of every section you have access to.' },
  {
    key: 'sales', feature: null, label: 'Sales', icon: TrendingUp,
    description: 'Sales detail, trends, lost business, and period comparisons.',
    subTabs: [
      { key: 'detail',      feature: 'analytics.sales',       label: 'Detail' },
      { key: 'trends',      feature: 'analytics.trends',      label: 'Trends' },
      { key: 'gaps',        feature: 'analytics.gaps',        label: 'Lost Business' },
      { key: 'compare7v28', feature: 'analytics.compare7v28', label: '7 vs 28 Days' },
      { key: 'yoy',         feature: 'analytics.yoy',         label: 'Overview YoY' },
      { key: 'vspayments',  feature: 'analytics.salespayments',  label: 'Vs Payments' },
      { key: 'gp',          feature: 'analytics.gp',             label: 'GP Analysis' },
      { key: 'margins',     feature: 'analytics.margins',        label: 'Margins' },
      { key: 'invoices',    feature: 'analytics.invoices',       label: 'Invoices' },
      { key: 'custweeks',   feature: 'analytics.custweeks',      label: 'Customer Weeks' },
      { key: 'snapshot',    feature: 'analytics.dailysnapshot',  label: 'Daily Snapshot' },
    ],
  },
  {
    key: 'ar', feature: null, label: 'A/R', icon: Receipt,
    description: 'Accounts receivable aging, drilldown, and payment insights.',
    subTabs: [
      { key: 'aging',    feature: 'analytics.ar',         label: 'Aging' },
      { key: 'insights', feature: 'analytics.arinsights', label: 'Insights' },
      { key: 'focus',    feature: 'analytics.arfocus',    label: 'Collections' },
      { key: 'ccfee',    feature: 'analytics.ccfee',      label: 'CC Fees' },
      { key: 'agingdate', feature: 'analytics.araging',   label: 'Aging by Date' },
      { key: 'qbodiff',  feature: 'analytics.qbodiff',    label: 'QuickBooks Diff' },
    ],
  },
  {
    key: 'ap', feature: null, label: 'A/P', icon: Wallet,
    description: 'Vendor bills, aging, spend, price changes, vendor performance and the QuickBooks comparison.',
    subTabs: [
      { key: 'aging',     feature: 'analytics.ap',      label: 'Aging' },
      { key: 'bills',     feature: 'analytics.ap',      label: 'Bills' },
      { key: 'spend',     feature: 'analytics.vendors', label: 'Vendor Spend' },
      { key: 'prices',    feature: 'analytics.vendors', label: 'Price Changes' },
      { key: 'scorecard', feature: 'analytics.vendors', label: 'Scorecard' },
      { key: 'qbodiff',   feature: 'analytics.qbodiff', label: 'QuickBooks Diff' },
    ],
  },
  {
    key: 'orders', feature: null, label: 'Orders', icon: ClipboardList,
    description: 'Order browsing plus operational audits.',
    subTabs: [
      { key: 'sheet',    feature: 'analytics.orders',   label: 'Order Sheet' },
      { key: 'opsaudit', feature: 'analytics.opsaudit', label: 'Ops Audit' },
      { key: 'transfers', feature: 'analytics.transfers', label: 'Transfers' },
    ],
  },
  {
    key: 'customers', feature: null, label: 'Customers', icon: UsersIcon,
    description: 'Customer-focused analytics.',
    subTabs: [
      { key: 'detail', feature: 'analytics.customers',      label: 'Detail' },
      { key: 'counts', feature: 'analytics.customercounts', label: 'Counts' },
    ],
  },
  { key: 'inventory',  feature: 'analytics.inventory', label: 'Inventory',  icon: Boxes,           description: 'Days of supply and replenishment alerts.' },
  { key: 'purchasing', feature: 'analytics.purchasing', label: 'Purchasing', icon: Truck,           description: 'Plan shipping containers and back-write to Advantage shpschd.' },
  { key: 'ai_orders',  feature: 'ai_orders',            label: 'AI Orders',  icon: Bot,             description: 'Review, edit, and approve orders captured by AI (voice + email).' },
];

interface AnalyticsHubPageProps {
  onBack?: () => void;
}

export default function AnalyticsHubPage({ onBack }: AnalyticsHubPageProps) {
  const { user, branding, claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const userRoles: string[] = claims?.roles || [];
  const showAll = userRoles.length === 0;
  const isSysadmin = hasAdminRole(userRoles);

  // Feature data — same shape as LandingPage uses for tile gating.
  const featuresQ = useQuery({
    queryKey: ['analytics-hub', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug ?? undefined),
    enabled: !!slug && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const effectiveQ = useQuery({
    queryKey: ['analytics-hub', 'effective', slug, user?.id],
    queryFn: () => adminApi.getEffectiveFeatures(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const overridesQ = useQuery({
    queryKey: ['analytics-hub', 'overrides', slug, user?.id],
    queryFn: () => adminApi.getAllUserOverrides(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });

  const effectiveSet  = new Set(effectiveQ.data ?? []);
  const dbFeatureKeys = new Set((featuresQ.data ?? []).map((f) => f.featureKey));
  const userOverrides = new Map<string, boolean>(
    (overridesQ.data ?? []).map((o) => [o.featureKey, o.isEnabled]),
  );
  const featuresLoading = featuresQ.isLoading || effectiveQ.isLoading || overridesQ.isLoading;
  const featuresErrored = !!(featuresQ.error || effectiveQ.error || overridesQ.error);

  function canSeeFeature(feature: string): boolean {
    if (showAll) return true;
    if (isSysadmin) return true;
    if (featuresLoading) return false;
    if (featuresErrored) {
      return isFeatureEnabled(feature, new Set(), new Set(), new Map());
    }
    return isFeatureEnabled(feature, effectiveSet, dbFeatureKeys, userOverrides);
  }

  function canSeeSection(s: SectionDef): boolean {
    // Grouped section: visible if ANY of its sub-tabs is visible.
    if (s.subTabs) return s.subTabs.some((t) => canSeeFeature(t.feature));
    if (s.feature === null) return true; // Overview always available if we got here
    return canSeeFeature(s.feature);
  }

  const visibleSections = SECTIONS.filter(canSeeSection);
  // null = the user hasn't picked a section yet — land on the first visible
  // one (Overview). If the global sidebar deep-linked a section, start there.
  // Once they click a section it sticks.
  // Active section is driven by the global app sidebar via the shared store
  // (the module no longer renders its own desktop rail — that was the double menu).
  const [storedSection, setStoredSection] = useSection('analyticsV2', 'overview');
  const activeSection = storedSection as Section;
  const setActiveSection = (s: Section) => setStoredSection(s);

  // Remember the selected sub-tab per grouped section; default = first visible.
  const [subTabBySection, setSubTabBySection] = useState<Partial<Record<Section, string>>>({});

  // If the active section becomes hidden (e.g. admin revoked it mid-session),
  // fall back to whatever's first in the visible list.
  const safeActive: Section =
    activeSection && visibleSections.some((s) => s.key === activeSection)
      ? activeSection
      : (visibleSections[0]?.key ?? 'overview');

  const activeDef = visibleSections.find((s) => s.key === safeActive);
  const visibleSubTabs = (activeDef?.subTabs ?? []).filter((t) => canSeeFeature(t.feature));
  const storedSubTab = subTabBySection[safeActive];
  const activeSubTab: string | null = visibleSubTabs.length === 0
    ? null
    : visibleSubTabs.some((t) => t.key === storedSubTab)
      ? storedSubTab!
      : visibleSubTabs[0].key;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1900px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                  title="Back to home"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                  <BarChart3 size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">Analytics</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile: horizontal scrollable tab strip (hidden at md+ where the
            sidebar takes over). Renders from the same visibleSections array
            as the sidebar so the gating logic is never duplicated. */}
        <nav className="md:hidden border-b border-slate-800/50 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ul className="flex items-center gap-1.5 w-max">
            {visibleSections.map((s) => {
              const Icon = s.icon;
              const isActive = safeActive === s.key;
              return (
                <li key={s.key} className="shrink-0">
                  <button
                    onClick={() => setActiveSection(s.key)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-blue-500/15 text-blue-200 border border-blue-500/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                    }`}
                  >
                    <Icon size={15} />
                    <span>{s.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile: second pill row with the active section's sub-tabs.
            Hidden when the section has 0-1 visible sub-tabs. */}
        {visibleSubTabs.length > 1 && (
          <nav className="md:hidden border-b border-slate-800/50 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <ul className="flex items-center gap-1.5 w-max">
              {visibleSubTabs.map((t) => {
                const isActive = activeSubTab === t.key;
                return (
                  <li key={t.key} className="shrink-0">
                    <button
                      onClick={() => setSubTabBySection((m) => ({ ...m, [safeActive]: t.key }))}
                      className={`px-3 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-blue-500/15 text-blue-200 border border-blue-500/30'
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                      }`}
                    >
                      {t.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        <div className="flex">
          {/* Main panel — top-level section nav is the global app sidebar now. */}
          <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
            {/* Desktop sub-tab strip for grouped sections. Skipped when only
                one sub-tab is visible — the content renders directly. */}
            {visibleSubTabs.length > 1 && (
              <div className="hidden md:flex items-center gap-1.5 mb-5 border-b border-slate-800/50 pb-3">
                {visibleSubTabs.map((t) => {
                  const isActive = activeSubTab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setSubTabBySection((m) => ({ ...m, [safeActive]: t.key }))}
                      className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-blue-500/15 text-blue-200 border border-blue-500/30'
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            {safeActive === 'overview'   && <OverviewSection visibleSections={visibleSections} onJump={(s) => setActiveSection(s)} />}
            {safeActive === 'sales'      && activeSubTab === 'detail' && (
              <SalesSection
                showAll={showAll}
                isSysadmin={isSysadmin}
                featuresLoading={featuresLoading}
                featuresErrored={featuresErrored}
                effectiveSet={effectiveSet}
                dbFeatureKeys={dbFeatureKeys}
                userOverrides={userOverrides}
              />
            )}
            {safeActive === 'sales'      && activeSubTab === 'trends'      && <SalesTrendsTab />}
            {safeActive === 'sales'      && activeSubTab === 'gaps'        && <LostBusinessTab />}
            {safeActive === 'sales'      && activeSubTab === 'compare7v28' && <SevenVs28Tab />}
            {safeActive === 'sales'      && activeSubTab === 'yoy'         && <YoYOverviewTab />}
            {safeActive === 'sales'      && activeSubTab === 'vspayments'  && <SalesVsPaymentsTab />}
            {safeActive === 'sales'      && activeSubTab === 'gp'          && <GpAnalysisTab />}
            {safeActive === 'sales'      && activeSubTab === 'margins'     && <MarginAnalysisTab />}
            {safeActive === 'sales'      && activeSubTab === 'invoices'    && <InvoicesTab />}
            {safeActive === 'sales'      && activeSubTab === 'custweeks'   && <CustomerWeeksTab />}
            {safeActive === 'sales'      && activeSubTab === 'snapshot'    && <DailySnapshotTab />}
            {safeActive === 'ar'         && activeSubTab === 'aging'       && <ARDetailTab />}
            {safeActive === 'ar'         && activeSubTab === 'insights'    && <ArInsightsTab />}
            {safeActive === 'ar'         && activeSubTab === 'focus'       && <ArFocusTab />}
            {safeActive === 'ar'         && activeSubTab === 'ccfee'       && <CcFeeTab />}
            {safeActive === 'ar'         && activeSubTab === 'agingdate'   && <AgingTab kind="ar" />}
            {safeActive === 'ar'         && activeSubTab === 'qbodiff'     && <QboDiffTab defaultEntity="invoices" />}
            {safeActive === 'ap'         && activeSubTab === 'aging'       && <AgingTab kind="ap" />}
            {safeActive === 'ap'         && activeSubTab === 'bills'       && <ApBillsTab />}
            {safeActive === 'ap'         && activeSubTab === 'spend'       && <VendorSpendTab />}
            {safeActive === 'ap'         && activeSubTab === 'prices'      && <VendorPriceChangesTab />}
            {safeActive === 'ap'         && activeSubTab === 'scorecard'   && <VendorScorecardTab />}
            {safeActive === 'ap'         && activeSubTab === 'qbodiff'     && <QboDiffTab defaultEntity="bills" />}
            {safeActive === 'orders'     && activeSubTab === 'sheet'       && <OrderSheetTab />}
            {safeActive === 'orders'     && activeSubTab === 'opsaudit'    && <OpsAuditTab />}
            {safeActive === 'orders'     && activeSubTab === 'transfers'   && <TransfersTab />}
            {safeActive === 'customers'  && activeSubTab === 'detail'      && <CustomerInsightsTab />}
            {safeActive === 'customers'  && activeSubTab === 'counts'      && <CustomerCountsTab />}
            {safeActive === 'inventory'  && <InventoryTab />}
            {safeActive === 'purchasing' && <PurchasingPipelineTab />}
            {safeActive === 'ai_orders'  && <AiOrdersTab />}
          </main>
        </div>
      </div>
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────

function OverviewSection({
  visibleSections,
  onJump,
}: {
  visibleSections: SectionDef[];
  onJump: (s: Section) => void;
}) {
  // Fetch the consolidated dashboard data only if Sales or AR are visible
  // (those are the cards that need real numbers; Customers/Inventory are
  // still placeholders).
  const showSales = visibleSections.some((s) => s.key === 'sales');
  const showAR    = visibleSections.some((s) => s.key === 'ar');
  const needData  = showSales || showAR;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['analytics-hub', 'overview-dashboard'],
    queryFn: () => api.getDashboardAll(),
    enabled: needData,
    retry: 1,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Overview</h2>
          <p className="text-sm text-slate-500">A snapshot of every section you have access to. Click a card to dive in.</p>
        </div>
        {needData && (
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Cards are filtered to whatever the user can see; Overview itself
            is excluded because clicking "Overview" from Overview is silly. */}
        {visibleSections.filter((s) => s.key !== 'overview').map((s) => {
          if (s.key === 'sales')      return <SalesOverviewCard key="sales" loading={isLoading} totalSales={data?.stats?.totalSales} repsCount={data?.salesByRep?.length} onJump={() => onJump('sales')} />;
          if (s.key === 'ar')         return <AROverviewCard    key="ar"    loading={isLoading} arData={data?.arAging} onJump={() => onJump('ar')} />;
          if (s.key === 'customers')  return <ComingSoonCard key="cust" label="Customers" onJump={() => onJump('customers')} />;
          if (s.key === 'inventory')  return <ComingSoonCard key="inv"  label="Inventory" onJump={() => onJump('inventory')} />;
          return null;
        })}
      </div>
    </div>
  );
}

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

function CardShell({
  accent, icon, title, onJump, children,
}: {
  accent: 'blue' | 'amber' | 'emerald' | 'fuchsia' | 'slate';
  icon: React.ReactNode;
  title: string;
  onJump: () => void;
  children: React.ReactNode;
}) {
  const tint: Record<string, string> = {
    blue:    'border-blue-500/30 bg-blue-500/5 hover:border-blue-400/40',
    amber:   'border-amber-500/30 bg-amber-500/5 hover:border-amber-400/40',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/40',
    fuchsia: 'border-fuchsia-500/30 bg-fuchsia-500/5 hover:border-fuchsia-400/40',
    slate:   'border-slate-700/40 bg-slate-900/40 hover:border-slate-600/50',
  };
  return (
    <button
      onClick={onJump}
      className={`text-left rounded-2xl border p-5 transition-all hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 ${tint[accent]}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-300">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronRight size={14} className="text-slate-500" />
      </div>
      {children}
    </button>
  );
}

function SalesOverviewCard({
  loading, totalSales, repsCount, onJump,
}: {
  loading: boolean;
  totalSales: number | undefined;
  repsCount: number | undefined;
  onJump: () => void;
}) {
  return (
    <CardShell accent="blue" icon={<TrendingUp size={16} />} title="Sales" onJump={onJump}>
      <div className="text-2xl font-semibold text-white tabular-nums">
        {loading ? '…' : (totalSales != null ? fmtCurrency(totalSales) : '—')}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {loading ? 'Loading…' : (repsCount != null ? `${repsCount} sales rep${repsCount === 1 ? '' : 's'}` : 'No data')}
      </div>
    </CardShell>
  );
}

function AROverviewCard({
  loading, arData, onJump,
}: {
  loading: boolean;
  arData: ARSummary | undefined;
  onJump: () => void;
}) {
  const total = arData?.totalAR ?? 0;
  // Buckets come back with arbitrary labels like "Current", "1-30", "31-60",
  // "61-90", "91-120", "120+". Pick out anything past 90 days for the
  // subtitle by matching the label loosely.
  const over90 = (arData?.buckets ?? [])
    .filter((b) => /(?:9[0-9]|1[0-9]{2,})/.test(b.label))
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const bucketCount = arData?.buckets?.length ?? 0;
  return (
    <CardShell accent="amber" icon={<Receipt size={16} />} title="AR" onJump={onJump}>
      <div className="text-2xl font-semibold text-white tabular-nums">
        {loading ? '…' : fmtCurrency(total)}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {loading
          ? 'Loading…'
          : over90 > 0
            ? `${fmtCurrency(over90)} over 90 days`
            : `${bucketCount} aging bucket${bucketCount === 1 ? '' : 's'}`}
      </div>
    </CardShell>
  );
}

function ComingSoonCard({ label, onJump }: { label: string; onJump: () => void }) {
  return (
    <CardShell accent="slate" icon={<LayoutDashboard size={16} />} title={label} onJump={onJump}>
      <div className="text-sm text-slate-400">Coming soon</div>
      <div className="text-[11px] text-slate-600 mt-1">Granted to your account — click to learn more.</div>
    </CardShell>
  );
}

// ── Sales section (sub-tabbed: Item Detail + By Rep) ────────────

interface SalesSectionProps {
  showAll: boolean;
  isSysadmin: boolean;
  featuresLoading: boolean;
  featuresErrored: boolean;
  effectiveSet: Set<string>;
  dbFeatureKeys: Set<string>;
  userOverrides: Map<string, boolean>;
}

function SalesSection({
  showAll, isSysadmin, featuresLoading, featuresErrored,
  effectiveSet, dbFeatureKeys, userOverrides,
}: SalesSectionProps) {
  // Gate the "By Rep" sub-tab on analytics.sales.by_rep
  const canSeeByRep =
    showAll || isSysadmin ||
    (!featuresLoading && !featuresErrored &&
      isFeatureEnabled('analytics.sales.by_rep', effectiveSet, dbFeatureKeys, userOverrides));

  type SubTab = 'item' | 'by_rep';
  const subTabs: { key: SubTab; label: string; visible: boolean }[] = [
    { key: 'item',   label: 'Item Detail', visible: true },
    { key: 'by_rep', label: 'By Rep',      visible: canSeeByRep },
  ].filter((t) => t.visible) as typeof subTabs;

  const [active, setActive] = useState<SubTab>('item');
  const safeActive: SubTab = subTabs.some((t) => t.key === active) ? active : subTabs[0].key;

  // No sub-tabs to render? Just show the legacy detail.
  if (subTabs.length <= 1) return <SalesDetailTab />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-slate-700/40">
        {subTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-3 py-2 text-sm transition-all border-b-2 -mb-px ${
              safeActive === t.key
                ? 'text-white border-blue-500'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {safeActive === 'item'   && <SalesDetailTab />}
      {safeActive === 'by_rep' && <SalesByRepTab />}
    </div>
  );
}
